import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, Marker, useMapEvents, useMap } from 'react-leaflet';
import { FiMapPin, FiSearch, FiUpload, FiTrash2, FiImage, FiExternalLink } from 'react-icons/fi';
import { createJob, updateJob, getClients, getTeamMembers, getJob, getAuthToken } from '../services/api';
import { API_URL } from '../config';
import { buildQPublicUrl, hasQPublicUrl } from '../utils/parcelUtils';
import axios from 'axios';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';

// Fix for default marker icons
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.7.1/images/marker-shadow.png',
});

// Component to handle map clicks
function MapClickHandler({ onMapClick }) {
  useMapEvents({
    click: (e) => {
      onMapClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Component to recenter map
function MapRecenter({ center }) {
  const map = useMap();
  useEffect(() => {
    if (center) {
      map.setView(center, 15);
    }
  }, [center, map]);
  return null;
}

// Inline turnaround estimate shown next to the service type dropdown
function ServiceTypeEstimate({ serviceType }) {
  const [estimate, setEstimate] = useState(null);
  const [loading, setLoading] = useState(false);
  useEffect(() => {
    if (!serviceType) { setEstimate(null); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const token = await getAuthToken();
        const config = { headers: token ? { Authorization: `Bearer ${token}` } : {} };
        const res = await axios.get(`${API_URL}/analytics/turnaround/estimate?service_type=${encodeURIComponent(serviceType)}`, config);
        if (!cancelled) setEstimate(res.data);
      } catch (e) {
        if (!cancelled) setEstimate(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [serviceType]);

  if (loading) return null;
  if (!estimate) return null;
  if (estimate.estimate_days == null) {
    return (
      <div style={{ marginTop: '6px', fontSize: '12px', color: '#6b7280', fontStyle: 'italic' }}>
        Not enough historical data yet for this service type ({estimate.sample_size} completed jobs so far — need 5+).
      </div>
    );
  }
  return (
    <div style={{ marginTop: '6px', fontSize: '12px', color: '#0369a1', backgroundColor: '#f0f9ff', border: '1px solid #bae6fd', padding: '6px 10px', borderRadius: '6px' }}>
      <strong>Typical turnaround:</strong> {estimate.estimate_days} days
      <span style={{ color: '#6b7280', marginLeft: '6px' }}>(median of {estimate.sample_size} similar completed jobs)</span>
    </div>
  );
}

function JobForm({ job, onSuccess, onCancel }) {
  const [formData, setFormData] = useState({
    job_number: '',
    client_id: '',
    property_address: '',
    parent_job_id: '',
    status: 'Pending',
    priority: 'Normal',
    scheduled_date: '',
    scheduled_time: '09:00',
    notes: '',
    latitude: '',
    longitude: '',
    // Work Order Fields
    work_ordered_date: '',
    work_started_date: '',
    work_finished_date: '',
    // Billing
    billing_person: '',
    billing_company: '',
    billing_address: '',
    billing_city_state_zip: '',
    billing_phone: '',
    billing_email: '',
    // Location Details
    location_person: '',
    subdivision: '',
    location_city_state_zip: '',
    parcel: '',
    county: '',
    land_lot: '',
    district: '',
    section: '',
    // Work Description
    service_type: '',
    description_of_work: '',
    job_specific_notes: '',
    // Completion
    surveyor_signature: '',
    completion_date: '',
    // Parcel Link
    parcel_url: ''
  });
  
  const [clients, setClients] = useState([]);
  const [teamMembers, setTeamMembers] = useState([]);
  const [assignedMembers, setAssignedMembers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [clientSearch, setClientSearch] = useState('');
  const [showClientDropdown, setShowClientDropdown] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    basic: true,
    billing: false,
    location: true,
    work: true,
    parcel: true,
    team: false,
    coordinates: false
  });
  const [showMapPicker, setShowMapPicker] = useState(false);
  const [mapCenter, setMapCenter] = useState([34.0522, -84.5]); // Georgia default
  const [searchAddress, setSearchAddress] = useState('');
  const [searching, setSearching] = useState(false);
  const [tempCoords, setTempCoords] = useState({ lat: null, lng: null });
  const [mapView, setMapView] = useState('aerial'); // 'street' or 'aerial'
  const [parcelImage, setParcelImage] = useState(null);
  const [parcelImageUrl, setParcelImageUrl] = useState(null);
  const [uploadingParcel, setUploadingParcel] = useState(false);
  const parcelInputRef = useRef(null);

  // Parent job state
  const [parentJobSearch, setParentJobSearch] = useState('');
  const [parentJobSuggestions, setParentJobSuggestions] = useState([]);
  const [showParentSuggestion, setShowParentSuggestion] = useState(false);

  useEffect(() => {
    loadClients();
    loadTeamMembers();
    
    if (job) {
      setFormData({
        job_number: job.job_number || '',
        client_id: job.client_id || '',
        property_address: job.property_address || '',
        parent_job_id: job.parent_job_id || '',
        status: job.status || 'Pending',
        priority: job.priority || 'Normal',
        scheduled_date: job.scheduled_date || '',
        scheduled_time: job.scheduled_time || '09:00',
        notes: job.notes || '',
        latitude: job.latitude || '',
        longitude: job.longitude || '',
        work_ordered_date: job.work_ordered_date || '',
        work_started_date: job.work_started_date || '',
        work_finished_date: job.work_finished_date || '',
        billing_person: job.billing_person || '',
        billing_company: job.billing_company || '',
        billing_address: job.billing_address || '',
        billing_city_state_zip: job.billing_city_state_zip || '',
        billing_phone: job.billing_phone || '',
        billing_email: job.billing_email || '',
        location_person: job.location_person || '',
        subdivision: job.subdivision || '',
        location_city_state_zip: job.location_city_state_zip || '',
        parcel: job.parcel || '',
        county: job.county || '',
        land_lot: job.land_lot || '',
        district: job.district || '',
        section: job.section || '',
        service_type: job.service_type || '',
        description_of_work: job.description_of_work || '',
        job_specific_notes: job.job_specific_notes || '',
        surveyor_signature: job.surveyor_signature || '',
        completion_date: job.completion_date || '',
        parcel_url: job.parcel_url || ''
      });
      if (job.team_members) {
        setAssignedMembers(job.team_members.map(tm => tm.id));
      }
      // Load parcel image if exists
      if (job.parcel_image_url) {
        setParcelImageUrl(`${API_URL}${job.parcel_image_url}`);
        setParcelImage(job.parcel_image);
      }
    }
  }, [job]);

  const loadClients = async () => {
    try {
      const response = await getClients();
      setClients(response.data.clients);
      
      // If editing a job, set the client search field
      if (job && job.client_id) {
        const client = response.data.clients.find(c => c.id === job.client_id);
        if (client) {
          setClientSearch(client.company_name || client.name);
        }
      }
    } catch (error) {
      console.error('Error loading clients:', error);
      setError('Failed to load clients');
    }
  };

  const loadTeamMembers = async () => {
    try {
      const response = await getTeamMembers();
      setTeamMembers(response.data.team_members);
    } catch (error) {
      console.error('Error loading team members:', error);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  // The client record keeps city, state, and zip in three separate columns; the job stores
  // them as one "City, ST 12345" string. Without this the billing City/State/Zip stayed
  // empty on every autofill. Missing pieces drop out instead of leaving stray punctuation.
  const clientCityStateZip = (client) => {
    const cityState = [client.city, client.state].filter(Boolean).join(', ');
    return [cityState, client.zip_code].filter(Boolean).join(' ').trim();
  };

  const handleClientSelect = (client) => {
    setFormData(prev => ({
      ...prev,
      client_id: client.id.toString(),
      billing_person: client.name || '',
      billing_company: client.company_name || '',
      billing_address: client.address || '',
      billing_city_state_zip: clientCityStateZip(client),
      billing_phone: client.phone || '',
      billing_email: client.email || '',
      // Seed the job-location contact person from the client too — only if not already set,
      // so a distinct on-site contact you typed isn't clobbered when picking the client.
      location_person: prev.location_person || client.name || ''
    }));
    setClientSearch(client.company_name || client.name);
    setShowClientDropdown(false);
  };

  const handleClientChange = (e) => {
    const clientId = e.target.value;
    setFormData(prev => ({ ...prev, client_id: clientId }));
    
    // Auto-fill billing info from client
    const selectedClient = clients.find(c => c.id.toString() === clientId);
    if (selectedClient) {
      setFormData(prev => ({
        ...prev,
        client_id: clientId,
        billing_person: selectedClient.name || '',
        billing_company: selectedClient.company_name || '',
        billing_address: selectedClient.address || '',
        billing_city_state_zip: clientCityStateZip(selectedClient),
        billing_phone: selectedClient.phone || '',
        billing_email: selectedClient.email || '',
        location_person: prev.location_person || selectedClient.name || ''
      }));
    }
  };

  // Filter clients based on search
  const filteredClients = clients.filter(client => {
    const search = clientSearch.toLowerCase();
    return (
      (client.name && client.name.toLowerCase().includes(search)) ||
      (client.company_name && client.company_name.toLowerCase().includes(search)) ||
      (client.email && client.email.toLowerCase().includes(search)) ||
      (client.phone && client.phone.includes(search))
    );
  });

  // Auto-detect parent job when job number has a letter suffix
  const checkForParentJob = async (jobNumber) => {
    if (!jobNumber || jobNumber.length < 3) return;
    // Check if job number ends with a letter (e.g., J4556B)
    const match = jobNumber.match(/^(.+?)([a-zA-Z]+)$/);
    if (!match) {
      setShowParentSuggestion(false);
      return;
    }
    try {
      const config = { headers: {} };
      const token = await getAuthToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      const response = await axios.get(`${API_URL}/jobs/suggest-parent?job_number=${encodeURIComponent(jobNumber)}`, config);
      const suggestions = response.data.suggestions || [];
      if (suggestions.length > 0 && !formData.parent_job_id) {
        setParentJobSuggestions(suggestions);
        setShowParentSuggestion(true);
      }
    } catch (err) {
      // Ignore errors
    }
  };

  // When a parent job is selected on a NEW job, pull its details and fill
  // any field the user hasn't already entered. Never overwrite existing input.
  const autoFillFromParent = async (parentId) => {
    if (!parentId || job) return; // only on create, not on edit
    try {
      const res = await getJob(parentId);
      const p = res?.data || {};
      const inheritable = [
        'client_id', 'property_address', 'subdivision', 'location_person', 'location_city_state_zip',
        'parcel', 'county', 'land_lot', 'district', 'section', 'parcel_url',
        'service_type', 'description_of_work', 'job_specific_notes',
        'billing_person', 'billing_company', 'billing_address', 'billing_city_state_zip',
        'billing_phone', 'billing_email',
        'latitude', 'longitude',
      ];
      setFormData(prev => {
        const next = { ...prev };
        for (const k of inheritable) {
          const cur = prev[k];
          const incoming = p[k];
          const isEmpty = cur === '' || cur === null || cur === undefined;
          if (isEmpty && incoming !== null && incoming !== undefined && incoming !== '') {
            next[k] = String(incoming);
          }
        }
        return next;
      });
      // Reflect the inherited client in the client search box so the chip renders
      if (p.client_id && p.client_name && !clientSearch) {
        setClientSearch(p.client_name);
      }
    } catch (err) {
      // Silent — auto-fill is a convenience, not a requirement
    }
  };

  const searchParentJobs = async (search) => {
    setParentJobSearch(search);
    if (search.length < 2) { setParentJobSuggestions([]); return; }
    try {
      const config = { headers: {} };
      const token = await getAuthToken();
      if (token) config.headers.Authorization = `Bearer ${token}`;
      const response = await axios.get(`${API_URL}/jobs?search=${encodeURIComponent(search)}&limit=10`, config);
      setParentJobSuggestions((response.data.jobs || []).filter(j => j.id !== job?.id));
    } catch (err) {
      // Ignore
    }
  };

  const handleTeamMemberToggle = (memberId) => {
    setAssignedMembers(prev => {
      if (prev.includes(memberId)) {
        return prev.filter(id => id !== memberId);
      } else {
        return [...prev, memberId];
      }
    });
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({ ...prev, [section]: !prev[section] }));
  };

  // Handle parcel image upload
  const handleParcelImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (PNG, JPG, GIF, WEBP)');
      return;
    }
    
    // If editing an existing job, upload immediately
    if (job && job.id) {
      setUploadingParcel(true);
      try {
        const formData = new FormData();
        formData.append('image', file);
        
        const token = await getAuthToken();
        const config = {
          withCredentials: true,
          headers: token ? { Authorization: `Bearer ${token}` } : {}
        };
        
        const response = await axios.post(`${API_URL}/jobs/${job.id}/parcel-image`, formData, config);
        setParcelImage(response.data.parcel_image);
        setParcelImageUrl(`${API_URL}${response.data.parcel_image_url}`);
      } catch (error) {
        console.error('Error uploading parcel image:', error);
        setError('Failed to upload parcel image');
      } finally {
        setUploadingParcel(false);
      }
    } else {
      // For new jobs, just store the file to upload after job creation
      setParcelImage(file);
      setParcelImageUrl(URL.createObjectURL(file));
    }
  };

  // Handle parcel image delete
  const handleParcelImageDelete = async () => {
    if (!job || !job.id) {
      // For new job, just clear the state
      setParcelImage(null);
      setParcelImageUrl(null);
      return;
    }
    
    try {
      const token = await getAuthToken();
      const config = {
        withCredentials: true,
        headers: token ? { Authorization: `Bearer ${token}` } : {}
      };
      
      await axios.delete(`${API_URL}/jobs/${job.id}/parcel-image`, config);
      setParcelImage(null);
      setParcelImageUrl(null);
    } catch (error) {
      console.error('Error deleting parcel image:', error);
      setError('Failed to delete parcel image');
    }
  };

  // Geocode address using Nominatim (free OpenStreetMap geocoder)
  const searchAddressOnMap = async () => {
    if (!searchAddress.trim()) return;
    
    setSearching(true);
    try {
      // Clean up the address
      let cleanAddress = searchAddress.trim().replace(/\s+/g, ' ');
      
      // Build search variations to try
      const searchVariations = [
        cleanAddress,
        // Remove directional suffixes (S.E., N.W., etc.)
        cleanAddress.replace(/\s+(S\.?E\.?|N\.?E\.?|S\.?W\.?|N\.?W\.?|SE|NE|SW|NW)\s*/gi, ' '),
        // Remove zip code
        cleanAddress.replace(/\s*\d{5}(-\d{4})?\s*$/, ''),
        // Just street and city/state (remove zip and clean directionals)
        cleanAddress.replace(/\s+(S\.?E\.?|N\.?E\.?|S\.?W\.?|N\.?W\.?|SE|NE|SW|NW)\s*/gi, ' ').replace(/\s*\d{5}(-\d{4})?\s*$/, '')
      ];
      
      // Remove duplicates
      const uniqueSearches = [...new Set(searchVariations.map(s => s.trim()))];
      
      console.log('Search variations:', uniqueSearches);
      
      let found = false;
      
      for (const searchTerm of uniqueSearches) {
        if (found) break;
        
        const url = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(searchTerm)}&limit=5`;
        console.log('Trying:', searchTerm);
        
        const response = await fetch(url);
        const results = await response.json();
        
        if (results && results.length > 0) {
          const { lat, lon, display_name } = results[0];
          const newLat = parseFloat(lat);
          const newLng = parseFloat(lon);
          console.log('Found:', display_name);
          setMapCenter([newLat, newLng]);
          setTempCoords({ lat: newLat, lng: newLng });
          found = true;
          break;
        }
      }
      
      if (!found) {
        // Last resort - try just city and state
        const parts = cleanAddress.split(',');
        if (parts.length >= 2) {
          const cityState = parts.slice(-2).join(',').trim().replace(/\s*\d{5}(-\d{4})?\s*$/, '');
          console.log('Trying city/state:', cityState);
          
          const fallbackUrl = `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(cityState)}&limit=1`;
          const fallbackResponse = await fetch(fallbackUrl);
          const fallbackResults = await fallbackResponse.json();
          
          if (fallbackResults && fallbackResults.length > 0) {
            const { lat, lon } = fallbackResults[0];
            setMapCenter([parseFloat(lat), parseFloat(lon)]);
            alert('Exact address not found in map database. Showing the general area - zoom in and click to set exact location.');
          } else {
            alert('Address not found. Please navigate manually and click on the map.');
          }
        } else {
          alert('Address not found. Please navigate manually and click on the map.');
        }
      }
    } catch (error) {
      console.error('Geocoding error:', error);
      alert('Error searching. Please navigate manually and click on the map.');
    }
    setSearching(false);
  };

  const handleMapClick = (lat, lng) => {
    setTempCoords({ lat, lng });
  };

  const openMapPicker = () => {
    // Initialize with current coords if they exist
    if (formData.latitude && formData.longitude) {
      const lat = parseFloat(formData.latitude);
      const lng = parseFloat(formData.longitude);
      setMapCenter([lat, lng]);
      setTempCoords({ lat, lng });
    } else if (formData.property_address) {
      setSearchAddress(formData.property_address);
    }
    setShowMapPicker(true);
  };

  const confirmMapSelection = () => {
    if (tempCoords.lat && tempCoords.lng) {
      setFormData(prev => ({
        ...prev,
        latitude: tempCoords.lat.toFixed(6),
        longitude: tempCoords.lng.toFixed(6)
      }));
    }
    setShowMapPicker(false);
    setTempCoords({ lat: null, lng: null });
    setSearchAddress('');
  };

  const cancelMapSelection = () => {
    setShowMapPicker(false);
    setTempCoords({ lat: null, lng: null });
    setSearchAddress('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const submitData = {
        ...formData,
        latitude: formData.latitude ? parseFloat(formData.latitude) : null,
        longitude: formData.longitude ? parseFloat(formData.longitude) : null,
        scheduled_date: formData.scheduled_date || null,
        scheduled_time: formData.scheduled_time || null,
        work_ordered_date: formData.work_ordered_date || null,
        work_started_date: formData.work_started_date || null,
        work_finished_date: formData.work_finished_date || null,
        completion_date: formData.completion_date || null,
        parent_job_id: formData.parent_job_id ? parseInt(formData.parent_job_id) : null,
        team_member_ids: assignedMembers
      };

      if (job) {
        await updateJob(job.id, submitData);
      } else {
        await createJob(submitData);
      }

      onSuccess();
    } catch (error) {
      console.error('Error saving job:', error);
      setError(error.response?.data?.error || 'Failed to save job');
    } finally {
      setLoading(false);
    }
  };

  const statusOptions = [
    'Pending',
    'Scheduled',
    'In Progress - Office Work',
    'In Progress - Initial Field Work',
    'In Progress - Topo',
    'In Progress - Additional Field Work Required',
    'In Progress - Drafting',
    'On Hold',
    'Survey Complete',
    'Needs Review',
    'Sent for Review',
    'Approved - Internal Review',
    'Approved - Planning & Zoning',
    'Submitted for Recording',
    'Recorded',
    'Final Mark Up / Stake Lines',
    'Needs Invoice',
    'Invoiced',
    'Paid'
  ];

  // A job already saved under a retired status (the old bare 'Approved', 'Imported') keeps
  // its own value in the list, or the select renders blank and saving rewrites the status.
  const statusChoices = formData.status && !statusOptions.includes(formData.status)
    ? [...statusOptions, formData.status]
    : statusOptions;

  const sectionStyle = {
    border: '1px solid #ddd',
    borderRadius: '8px',
    marginBottom: '15px',
    overflow: 'hidden'
  };

  const headerStyle = {
    background: '#f5f5f5',
    padding: '12px 15px',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    fontWeight: '600'
  };

  const contentStyle = {
    padding: '15px'
  };

  return (
    <div className="job-form">
      <h2>{job ? 'Edit Job' : 'Create New Job'}</h2>
      
      {error && <div className="alert alert-error">{error}</div>}
      
      <form onSubmit={handleSubmit}>
        
        {/* Basic Information */}
        <div style={sectionStyle}>
          <div style={headerStyle} onClick={() => toggleSection('basic')}>
            <span>Basic Information</span>
            <span>{expandedSections.basic ? '−' : '+'}</span>
          </div>
          {expandedSections.basic && (
            <div style={contentStyle}>
              <div className="form-row">
                <div className="form-group">
                  <label>Job Number *</label>
                  <input
                    type="text"
                    name="job_number"
                    value={formData.job_number}
                    onChange={handleChange}
                    onBlur={(e) => checkForParentJob(e.target.value)}
                    required
                    placeholder="e.g., SUR-2025-001"
                    className="form-control"
                  />
                  {/* Auto-detected parent suggestion */}
                  {showParentSuggestion && !formData.parent_job_id && parentJobSuggestions.length > 0 && (
                    <div style={{
                      marginTop: '6px', padding: '10px', backgroundColor: '#eff6ff',
                      border: '1px solid #bfdbfe', borderRadius: '6px', fontSize: '13px'
                    }}>
                      <strong>Link to parent job?</strong>
                      {parentJobSuggestions.filter(s => s.is_exact_base).map(s => (
                        <button
                          key={s.id} type="button"
                          onClick={() => { setFormData(prev => ({ ...prev, parent_job_id: s.id.toString() })); setParentJobSearch(s.job_number); setShowParentSuggestion(false); autoFillFromParent(s.id); }}
                          style={{
                            display: 'block', width: '100%', marginTop: '6px', padding: '8px',
                            backgroundColor: 'white', border: '1px solid #93c5fd', borderRadius: '6px',
                            cursor: 'pointer', textAlign: 'left', fontSize: '13px'
                          }}
                        >
                          <strong>{s.job_number}</strong> — {s.client_name} {s.property_address ? `(${s.property_address})` : ''}
                        </button>
                      ))}
                      <button type="button" onClick={() => setShowParentSuggestion(false)}
                        style={{ marginTop: '6px', background: 'none', border: 'none', color: '#6b7280', cursor: 'pointer', fontSize: '12px' }}>
                        No, this is a standalone job
                      </button>
                    </div>
                  )}
                </div>
                <div className="form-group" style={{ position: 'relative' }}>
                  <label>Client *</label>
                  <input
                    type="text"
                    value={clientSearch}
                    onChange={(e) => {
                      setClientSearch(e.target.value);
                      setShowClientDropdown(true);
                      if (!e.target.value) {
                        setFormData(prev => ({ ...prev, client_id: '' }));
                      }
                    }}
                    onFocus={() => setShowClientDropdown(true)}
                    placeholder="Type to search clients..."
                    className="form-control"
                    autoComplete="off"
                  />
                  <input type="hidden" name="client_id" value={formData.client_id} required />
                  {showClientDropdown && (
                    <div style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      right: 0,
                      maxHeight: '200px',
                      overflowY: 'auto',
                      backgroundColor: 'white',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      zIndex: 1000
                    }}>
                      {filteredClients.length > 0 ? (
                        filteredClients.slice(0, 20).map(client => (
                          <div
                            key={client.id}
                            onClick={() => handleClientSelect(client)}
                            style={{
                              padding: '10px 12px',
                              cursor: 'pointer',
                              borderBottom: '1px solid #eee',
                              backgroundColor: formData.client_id === client.id.toString() ? '#e3f2fd' : 'white'
                            }}
                            onMouseEnter={(e) => e.target.style.backgroundColor = '#f5f5f5'}
                            onMouseLeave={(e) => e.target.style.backgroundColor = formData.client_id === client.id.toString() ? '#e3f2fd' : 'white'}
                          >
                            <div style={{ fontWeight: '500' }}>
                              {client.company_name || client.name}
                            </div>
                            {client.company_name && (
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                Contact: {client.name}
                              </div>
                            )}
                            {client.phone && (
                              <div style={{ fontSize: '11px', color: '#999' }}>
                                {client.phone}
                              </div>
                            )}
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: '10px 12px', color: '#999' }}>
                          No clients found
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Status *</label>
                  <select
                    name="status"
                    value={formData.status}
                    onChange={handleChange}
                    required
                    className="form-control"
                  >
                    {statusChoices.map(status => (
                      <option key={status} value={status}>{status}</option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label>Priority</label>
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                    className="form-control"
                  >
                    <option value="Low">Low</option>
                    <option value="Normal">Normal</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </div>
              </div>

              {/* Parent Job Link */}
              <div className="form-group" style={{ position: 'relative' }}>
                <label>Parent Job (optional)</label>
                {formData.parent_job_id ? (
                  <div style={{
                    padding: '8px 12px', backgroundColor: '#eff6ff', border: '1px solid #bfdbfe',
                    borderRadius: '6px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                  }}>
                    <span style={{ fontWeight: '500' }}>
                      {parentJobSearch || job?.parent_job_number || `Job #${formData.parent_job_id}`}
                    </span>
                    <button type="button" onClick={() => { setFormData(prev => ({ ...prev, parent_job_id: '' })); setParentJobSearch(''); }}
                      style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#dc2626', fontSize: '16px' }}>
                      ✕
                    </button>
                  </div>
                ) : (
                  <>
                    <input
                      type="text"
                      value={parentJobSearch}
                      onChange={(e) => searchParentJobs(e.target.value)}
                      placeholder="Search by job number to link..."
                      className="form-control"
                    />
                    {parentJobSearch.length >= 2 && parentJobSuggestions.length > 0 && (
                      <div style={{
                        position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 10,
                        backgroundColor: 'white', border: '1px solid #ddd', borderRadius: '6px',
                        maxHeight: '200px', overflowY: 'auto', boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
                      }}>
                        {parentJobSuggestions.map(s => (
                          <div
                            key={s.id}
                            onClick={() => {
                              setFormData(prev => ({ ...prev, parent_job_id: s.id.toString() }));
                              setParentJobSearch(s.job_number);
                              setParentJobSuggestions([]);
                              autoFillFromParent(s.id);
                            }}
                            style={{
                              padding: '10px 12px', cursor: 'pointer', borderBottom: '1px solid #f0f0f0',
                              fontSize: '13px'
                            }}
                          >
                            <strong>{s.job_number}</strong>
                            {s.client_name && <span style={{ color: '#666' }}> — {s.client_name}</span>}
                            {s.property_address && <div style={{ color: '#999', fontSize: '12px' }}>{s.property_address}</div>}
                          </div>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Work Ordered Date</label>
                  <input
                    type="date"
                    name="work_ordered_date"
                    value={formData.work_ordered_date}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Work Started Date</label>
                  <input
                    type="date"
                    name="work_started_date"
                    value={formData.work_started_date}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Work Finished Date</label>
                  <input
                    type="date"
                    name="work_finished_date"
                    value={formData.work_finished_date}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Scheduled Date</label>
                  <input
                    type="date"
                    name="scheduled_date"
                    value={formData.scheduled_date}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Scheduled Time</label>
                  <input
                    type="time"
                    name="scheduled_time"
                    value={formData.scheduled_time}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Billing Information */}
        <div style={sectionStyle}>
          <div style={headerStyle} onClick={() => toggleSection('billing')}>
            <span>Billing Information</span>
            <span>{expandedSections.billing ? '−' : '+'}</span>
          </div>
          {expandedSections.billing && (
            <div style={contentStyle}>
              <div className="form-row">
                <div className="form-group">
                  <label>Person</label>
                  <input
                    type="text"
                    name="billing_person"
                    value={formData.billing_person}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Company</label>
                  <input
                    type="text"
                    name="billing_company"
                    value={formData.billing_company}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Address</label>
                <input
                  type="text"
                  name="billing_address"
                  value={formData.billing_address}
                  onChange={handleChange}
                  className="form-control"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>City/State/Zip</label>
                  <input
                    type="text"
                    name="billing_city_state_zip"
                    value={formData.billing_city_state_zip}
                    onChange={handleChange}
                    placeholder="City, ST 12345"
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Phone</label>
                  <input
                    type="tel"
                    name="billing_phone"
                    value={formData.billing_phone}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="billing_email"
                    value={formData.billing_email}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Job Location */}
        <div style={sectionStyle}>
          <div style={headerStyle} onClick={() => toggleSection('location')}>
            <span>Job Location</span>
            <span>{expandedSections.location ? '−' : '+'}</span>
          </div>
          {expandedSections.location && (
            <div style={contentStyle}>
              <div className="form-row">
                <div className="form-group">
                  <label>Contact Person</label>
                  <input
                    type="text"
                    name="location_person"
                    value={formData.location_person}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Subdivision</label>
                  <input
                    type="text"
                    name="subdivision"
                    value={formData.subdivision}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Property Address</label>
                <input
                  type="text"
                  name="property_address"
                  value={formData.property_address}
                  onChange={handleChange}
                  placeholder="123 Main Street"
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>City/State/Zip</label>
                <input
                  type="text"
                  name="location_city_state_zip"
                  value={formData.location_city_state_zip}
                  onChange={handleChange}
                  placeholder="City, ST 12345"
                  className="form-control"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Parcel</label>
                  <input
                    type="text"
                    name="parcel"
                    value={formData.parcel}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>County</label>
                  <input
                    type="text"
                    name="county"
                    value={formData.county}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>

              {/* qPublic Parcel Lookup Link */}
              {hasQPublicUrl(formData.county) && !formData.parcel_url && (
                <div style={{ marginBottom: '10px' }}>
                  <a
                    href={buildQPublicUrl(formData.county)}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '8px',
                      padding: '8px 14px',
                      backgroundColor: '#fff7ed',
                      color: '#ea580c',
                      border: '1px solid #fed7aa',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontSize: '13px',
                      fontWeight: '500'
                    }}
                  >
                    <FiExternalLink size={14} />
                    Look up parcel in {formData.county} qPublic
                  </a>
                </div>
              )}

              {/* Parcel URL input */}
              <div className="form-group" style={{ marginBottom: '15px' }}>
                <label>Parcel URL</label>
                <div style={{ fontSize: '12px', color: '#6b7280', marginBottom: '4px' }}>
                  Paste the direct link to this parcel from qPublic
                </div>
                <input
                  type="url"
                  name="parcel_url"
                  value={formData.parcel_url}
                  onChange={handleChange}
                  placeholder="https://qpublic.schneidercorp.com/..."
                  className="form-control"
                />
                {formData.parcel_url && (
                  <a
                    href={formData.parcel_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '6px',
                      marginTop: '8px',
                      padding: '6px 12px',
                      backgroundColor: '#dcfce7',
                      color: '#166534',
                      border: '1px solid #86efac',
                      borderRadius: '6px',
                      textDecoration: 'none',
                      fontSize: '12px',
                      fontWeight: '500'
                    }}
                  >
                    <FiExternalLink size={12} />
                    Open saved parcel link
                  </a>
                )}
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label>Land Lot</label>
                  <input
                    type="text"
                    name="land_lot"
                    value={formData.land_lot}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>District</label>
                  <input
                    type="text"
                    name="district"
                    value={formData.district}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Section</label>
                  <input
                    type="text"
                    name="section"
                    value={formData.section}
                    onChange={handleChange}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Work Description */}
        <div style={sectionStyle}>
          <div style={headerStyle} onClick={() => toggleSection('work')}>
            <span>Work Description</span>
            <span>{expandedSections.work ? '−' : '+'}</span>
          </div>
          {expandedSections.work && (
            <div style={contentStyle}>
              <div className="form-group">
                <label>Service Type</label>
                <select
                  name="service_type"
                  value={formData.service_type}
                  onChange={handleChange}
                  className="form-control"
                >
                  <option value="">— Select service type —</option>
                  <option value="Boundary">Boundary</option>
                  <option value="Topographic">Topographic</option>
                  <option value="ALTA">ALTA / NSPS</option>
                  <option value="Construction Stakeout">Construction Stakeout</option>
                  <option value="As-Built">As-Built</option>
                  <option value="Foundation">Foundation</option>
                  <option value="Subdivision/Plat">Subdivision / Plat</option>
                  <option value="Elevation Certificate">Elevation Certificate</option>
                  <option value="Mortgage Inspection">Mortgage Inspection</option>
                  <option value="Site Plan">Site Plan</option>
                  <option value="Other">Other</option>
                </select>
                {formData.service_type && (
                  <ServiceTypeEstimate serviceType={formData.service_type} />
                )}
              </div>

              <div className="form-group">
                <label>Description of Work Required</label>
                <textarea
                  name="description_of_work"
                  value={formData.description_of_work}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Describe the survey work to be performed..."
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>Access Notes</label>
                <textarea
                  name="job_specific_notes"
                  value={formData.job_specific_notes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Gate codes, access instructions, where to park, who to call on-site..."
                  className="form-control"
                />
              </div>

              <div className="form-group">
                <label>General Notes</label>
                <textarea
                  name="notes"
                  value={formData.notes}
                  onChange={handleChange}
                  rows="3"
                  placeholder="Additional notes..."
                  className="form-control"
                />
              </div>
            </div>
          )}
        </div>

        {/* Parcel Image */}
        <div style={sectionStyle}>
          <div style={headerStyle} onClick={() => toggleSection('parcel')}>
            <span>Parcel Image (for Work Order)</span>
            <span>{expandedSections.parcel ? '−' : '+'}</span>
          </div>
          {expandedSections.parcel && (
            <div style={contentStyle}>
              <p style={{ fontSize: '13px', color: '#666', marginBottom: '15px' }}>
                Upload an image of the parcel/property. This image will appear on printed work orders.
              </p>
              
              {parcelImageUrl ? (
                <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start', gap: '15px' }}>
                  <div style={{
                    border: '2px solid #ddd',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    maxWidth: '400px'
                  }}>
                    <img 
                      src={parcelImageUrl} 
                      alt="Parcel" 
                      style={{ width: '100%', height: 'auto', display: 'block' }}
                    />
                  </div>
                  <div style={{ display: 'flex', gap: '10px' }}>
                    <button
                      type="button"
                      onClick={() => parcelInputRef.current?.click()}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        backgroundColor: '#1976d2',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      <FiUpload /> Replace Image
                    </button>
                    <button
                      type="button"
                      onClick={handleParcelImageDelete}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        padding: '8px 16px',
                        backgroundColor: '#dc3545',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        fontSize: '13px'
                      }}
                    >
                      <FiTrash2 /> Remove
                    </button>
                  </div>
                </div>
              ) : (
                <div 
                  onClick={() => parcelInputRef.current?.click()}
                  style={{
                    border: '2px dashed #ccc',
                    borderRadius: '8px',
                    padding: '40px 20px',
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: '#f9f9f9',
                    maxWidth: '400px'
                  }}
                >
                  <FiImage size={48} color="#999" />
                  <p style={{ marginTop: '10px', color: '#666' }}>
                    {uploadingParcel ? 'Uploading...' : 'Click to upload parcel image'}
                  </p>
                  <p style={{ fontSize: '12px', color: '#999', marginTop: '5px' }}>
                    PNG, JPG, GIF, WEBP accepted
                  </p>
                </div>
              )}
              
              <input
                ref={parcelInputRef}
                type="file"
                accept="image/*"
                onChange={handleParcelImageUpload}
                style={{ display: 'none' }}
              />
            </div>
          )}
        </div>

        {/* Team Assignment */}
        <div style={sectionStyle}>
          <div style={headerStyle} onClick={() => toggleSection('team')}>
            <span>Assign Team Members ({assignedMembers.length} selected)</span>
            <span>{expandedSections.team ? '−' : '+'}</span>
          </div>
          {expandedSections.team && (
            <div style={contentStyle}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '10px' }}>
                {teamMembers.map(member => (
                  <label key={member.id} style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={assignedMembers.includes(member.id)}
                      onChange={() => handleTeamMemberToggle(member.id)}
                    />
                    <span>{member.name} - {member.role}</span>
                  </label>
                ))}
              </div>
              {teamMembers.length === 0 && (
                <p style={{ color: '#666', fontStyle: 'italic' }}>No team members available. Add team members in the Team section.</p>
              )}
            </div>
          )}
        </div>

        {/* Map Coordinates */}
        <div style={sectionStyle}>
          <div style={headerStyle} onClick={() => toggleSection('coordinates')}>
            <span>Map Coordinates (Optional)</span>
            <span>{expandedSections.coordinates ? '−' : '+'}</span>
          </div>
          {expandedSections.coordinates && (
            <div style={contentStyle}>
              <div style={{ marginBottom: '15px' }}>
                <button
                  type="button"
                  onClick={openMapPicker}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                    padding: '10px 20px',
                    backgroundColor: '#1976d2',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer',
                    fontSize: '14px',
                    fontWeight: '500'
                  }}
                >
                  <FiMapPin /> Pick Location on Map
                </button>
              </div>
              <div className="form-row">
                <div className="form-group">
                  <label>Latitude</label>
                  <input
                    type="number"
                    step="any"
                    name="latitude"
                    value={formData.latitude}
                    onChange={handleChange}
                    placeholder="e.g., 34.0522"
                    className="form-control"
                  />
                </div>
                <div className="form-group">
                  <label>Longitude</label>
                  <input
                    type="number"
                    step="any"
                    name="longitude"
                    value={formData.longitude}
                    onChange={handleChange}
                    placeholder="e.g., -84.5000"
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="form-actions">
          <button 
            type="button" 
            onClick={onCancel}
            className="btn btn-secondary"
            disabled={loading}
          >
            Cancel
          </button>
          <button 
            type="submit" 
            className="btn btn-primary"
            disabled={loading}
          >
            {loading ? 'Saving...' : (job ? 'Update Job' : 'Create Job')}
          </button>
        </div>
      </form>

      <div className="form-help">
        <p><strong>Google Calendar Sync:</strong> When you set the status to "Scheduled" and add a date/time, this job will automatically sync to Google Calendar (if enabled in Settings).</p>
      </div>

      {/* Map Picker Modal */}
      {showMapPicker && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundColor: 'rgba(0, 0, 0, 0.6)',
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          zIndex: 2000
        }}>
          <div style={{
            backgroundColor: 'white',
            borderRadius: '12px',
            width: '90%',
            maxWidth: '900px',
            height: '80vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden'
          }}>
            {/* Modal Header */}
            <div style={{
              padding: '15px 20px',
              borderBottom: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <h3 style={{ margin: 0 }}>Pick Location on Map</h3>
              <button
                type="button"
                onClick={cancelMapSelection}
                style={{
                  background: 'none',
                  border: 'none',
                  fontSize: '24px',
                  cursor: 'pointer',
                  color: '#666'
                }}
              >
                &times;
              </button>
            </div>

            {/* Search Bar */}
            <div style={{
              padding: '15px 20px',
              borderBottom: '1px solid #eee',
              display: 'flex',
              gap: '10px'
            }}>
              <input
                type="text"
                value={searchAddress}
                onChange={(e) => setSearchAddress(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), searchAddressOnMap())}
                placeholder="Search address (e.g., 123 Main St, Atlanta, GA)"
                style={{
                  flex: 1,
                  padding: '10px 15px',
                  border: '1px solid #ddd',
                  borderRadius: '6px',
                  fontSize: '14px'
                }}
              />
              <button
                type="button"
                onClick={searchAddressOnMap}
                disabled={searching}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                  padding: '10px 20px',
                  backgroundColor: '#28a745',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: searching ? 'not-allowed' : 'pointer',
                  opacity: searching ? 0.7 : 1
                }}
              >
                <FiSearch />
                {searching ? 'Searching...' : 'Search'}
              </button>
            </div>

            {/* Map Container */}
            <div style={{ flex: 1, position: 'relative' }}>
              <MapContainer
                center={mapCenter}
                zoom={12}
                style={{ height: '100%', width: '100%' }}
              >
                {mapView === 'aerial' ? (
                  <TileLayer
                    attribution='&copy; <a href="https://www.esri.com/">Esri</a>'
                    url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
                  />
                ) : (
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                )}
                <MapClickHandler onMapClick={handleMapClick} />
                <MapRecenter center={mapCenter} />
                {tempCoords.lat && tempCoords.lng && (
                  <Marker position={[tempCoords.lat, tempCoords.lng]} />
                )}
              </MapContainer>
              
              {/* View Toggle */}
              <div style={{
                position: 'absolute',
                top: '10px',
                right: '10px',
                zIndex: 1000,
                display: 'flex',
                borderRadius: '6px',
                overflow: 'hidden',
                boxShadow: '0 2px 6px rgba(0,0,0,0.3)'
              }}>
                <button
                  type="button"
                  onClick={() => setMapView('street')}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: mapView === 'street' ? '#1976d2' : 'white',
                    color: mapView === 'street' ? 'white' : '#333',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  Street
                </button>
                <button
                  type="button"
                  onClick={() => setMapView('aerial')}
                  style={{
                    padding: '8px 14px',
                    backgroundColor: mapView === 'aerial' ? '#1976d2' : 'white',
                    color: mapView === 'aerial' ? 'white' : '#333',
                    border: 'none',
                    borderLeft: '1px solid #ddd',
                    cursor: 'pointer',
                    fontSize: '13px',
                    fontWeight: '500'
                  }}
                >
                  Aerial
                </button>
              </div>
              
              {/* Instructions overlay */}
              <div style={{
                position: 'absolute',
                top: '10px',
                left: '50%',
                transform: 'translateX(-50%)',
                backgroundColor: 'rgba(0, 0, 0, 0.7)',
                color: 'white',
                padding: '8px 16px',
                borderRadius: '20px',
                fontSize: '13px',
                zIndex: 1000,
                pointerEvents: 'none'
              }}>
                Click on the map to set location
              </div>
            </div>

            {/* Modal Footer */}
            <div style={{
              padding: '15px 20px',
              borderTop: '1px solid #ddd',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center'
            }}>
              <div style={{ fontSize: '14px', color: '#666' }}>
                {tempCoords.lat && tempCoords.lng ? (
                  <span>
                    Selected: <strong>{tempCoords.lat.toFixed(6)}, {tempCoords.lng.toFixed(6)}</strong>
                  </span>
                ) : (
                  <span>No location selected</span>
                )}
              </div>
              <div style={{ display: 'flex', gap: '10px' }}>
                <button
                  type="button"
                  onClick={cancelMapSelection}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: '#6c757d',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: 'pointer'
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={confirmMapSelection}
                  disabled={!tempCoords.lat || !tempCoords.lng}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: tempCoords.lat ? '#28a745' : '#ccc',
                    color: 'white',
                    border: 'none',
                    borderRadius: '6px',
                    cursor: tempCoords.lat ? 'pointer' : 'not-allowed'
                  }}
                >
                  Confirm Location
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default JobForm;